import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminSupportExtra } from "../../../../../http/patrol/types/supportExtras";
import { useSupportExtrasApi } from "../../../../../http/patrol/useSupportExtras";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import AddSupportExtraModalDialogButton from "./AddSupportExtraModalDialogButton";
import SupportExtraForm from "./SupportExtraForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const SupportExtras: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "supportExtras" });
  const aborter = useRef(new AbortController());
  const supportExtrasApi = useSupportExtrasApi();
  const { setActions, setTitle } = useSearchContext();

  const [itemToEdit, setItemToEdit] = useState<AdminSupportExtra>();
  const [itemToDelete, setItemToDelete] = useState<AdminSupportExtra>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "supportExtras");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = supportExtrasApi.useAdminSupportExtras(query, aborter.current.signal, patrolSystemId);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.supportExtras.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.supportExtras.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <AddSupportExtraModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminSupportExtra>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            {!patrolSystemId && <th align="left">{i18n.translation.patrolSystems.singular}</th>}
            <th align="left">{i18n.translation.roofSubstructures.singular}</th>
            <th align="left">{i18n.translation.common.seamHeight}</th>
            <th align="left">{i18n.translation.common.minThickness}</th>
            <th align="left">{i18n.translation.common.maxThickness}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.item.id}`}>
                {item.item.code}
              </Link>
            </td>
            <td>{item.item.description}</td>
            {!patrolSystemId && <td>{item.system.name}</td>}
            <td>{item.roofSubstructure?.name}</td>
            <td>{item.seamHeight ? `${item.seamHeight} mm` : undefined}</td>
            <td>{item.minThickness ? `${item.minThickness} mm` : undefined}</td>
            <td>{item.maxThickness ? `${item.maxThickness} mm` : undefined}</td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.supportExtras.edit}>
          <SupportExtraForm onClose={() => setItemToEdit(undefined)} defaultValues={itemToEdit} />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.supportExtras.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                supportExtrasApi
                  .deleteAdminSupportExtra(itemToDelete.id, itemToDelete.system.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.supportExtras.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default SupportExtras;
